import { useTheme } from "@repo/tokens";
import type { HeaderWebStyles } from "./types";
import type { CSSProperties } from "react";

export const useHeaderStyles = (props?: {
  padding?: number;
  titleColor?: string;
  headerHeight?: number;
}): HeaderWebStyles => {
  const { theme } = useTheme();
  const { padding, titleColor, headerHeight } = props || {};

  return {
    container: {
      height: headerHeight ? `${headerHeight / 16}rem` : "3.5rem",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      padding: padding ? `${padding / 16}rem` : 0,
      width: "100%",
    },

    section: {
      display: "flex",
      alignItems: "center",
    },

    sectionLeft: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: theme.spacing.lg,
    },

    sectionRight: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
    },

    center: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },

    title: {
      ...theme.typography.title.medium.regular,
      color: titleColor ?? theme.text.primary,
      textAlign: "center",
      letterSpacing: theme.typography.body.large.regular.letterSpacing,
    },

    subtitle: {
      ...theme.typography.body.small.regular,
      color: theme.text.secondary,
      textAlign: "center",
      marginTop: theme.spacing.xs,
    },

    rightActionContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing.xs,
    },

    rightActionLabel: {
      ...theme.typography.label.small.regular,
      color: theme.action.primary.default,
    },
    drawerOverlay: {
      position: "fixed",
      inset: 0,
      zIndex: 998,
    },

    drawerBase: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "17.5rem",
      height: "100vh",
      backgroundColor: theme.surface.card,
      zIndex: 999,
      display: "flex",
      flexDirection: "column",
      transition: "transform 0.25s ease-out",
    },

    drawerClosed: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "17.5rem",
      height: "100vh",
      backgroundColor: theme.surface.card,
      zIndex: 999,
      display: "flex",
      flexDirection: "column",
      transform: "translateY(100%)",
      transition: "transform 0.25s ease-out",
    },

    drawerOpen: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100vh",
      backgroundColor: theme.surface.card,
      zIndex: 999,
      display: "flex",
      flexDirection: "column",
      transform: "translateY(0)",
      transition: "transform 0.25s ease-out",
    },

    drawerHeader: {
      height: "3.5rem",
      paddingInline: theme.spacing.md,
      display: "flex",
      alignItems: "center",
      borderBottom: `0.0625rem solid ${theme.border.default}`,
    },

    drawerTitle: {
      ...theme.typography.title.small.emphasized,
      color: theme.text.primary,
    },

    drawerContent: {
      flex: 1,
      padding: theme.spacing.md,
    },

    rightPopoverWrapper: {
      position: "relative",
    },

    rightPopover: {
      position: "absolute",
      top: "100%",
      right: 0,
      marginTop: "0.5rem",
      backgroundColor: "transparent",
      zIndex: 1000,
    },

    rightPopoverItem: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "flex-end",
      backgroundColor: theme.colors.primary[95],
      borderRadius: "6.25rem",
      padding: "0.8125rem 1.5rem",
      fontSize: theme.typography.title.medium.regular.fontSize,
      color: theme.colors.primary[10],
      lineHeight: theme.typography.title.medium.regular.lineHeight,
      letterSpacing: theme.typography.title.medium.regular.lineHeight,
      cursor: "pointer",
      fontWeight: 500,
      textAlign: "center",
    },
    rightPopoverList: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: "0.75rem",
    },

    headerButton: {
      backgroundColor: "transparent",
      outline: "none",
      border: "none",
      padding: 0,
      boxShadow: "none",
      cursor: "pointer",
      display: "flex",
      gap: "0.5rem",
      paddingInline: 0,
    } as CSSProperties,

    rightArrow: {
      display: "inline-flex",
      transition: "transform 150ms ease-out",
    },

    drawerModalOverlay: {
      flex: 1,
      backgroundColor: "transparent",
      justifyContent: "flex-end",
      alignItems: "center",
    },

    drawerModal: {
      width: "100%",
      maxHeight: "100dvh",
      height: "auto",
      padding: 0,
      boxSizing: "border-box" as const,
      backgroundColor: "transparent",
      borderRadius: "2.5rem",
      borderTopLeftRadius: "2.5rem",
      borderTopRightRadius: "2.5rem",
      borderBottomLeftRadius: "0rem",
      borderBottomRightRadius: "0rem",
      overflowX: "hidden",
      overflowY: "hidden",
      boxShadow: theme.shadows.lg,
      transition: "transform 0.25s ease-out",
      willChange: "transform",
    },

    drawerModalClosed: {
      transform: "translateY(100%)",
    },

    drawerModalOpen: {
      transform: "translateY(0)",
    },

    drawerModalBody: {
      flexGrow: 0,
      flexShrink: 0,
      overflow: "visible",
      padding: 0,
    },

    rightIconWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.5rem",
    },
    iconShadow: {
      display: "inline-flex",
      borderRadius: "6.5rem",
      boxShadow: `
    0rem 0.0625rem 0.1875rem 0rem #0000004D,
    0rem 0.25rem 0.5rem 0.1875rem #00000026
  `,
    },

    rightAvatarCircle: {
      width: "3rem",
      height: "3rem",
      borderRadius: "1.5rem",
      backgroundColor: theme.colors.tertiary[95],
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      overflow: "hidden",
    } as CSSProperties,

    rightAvatarInitialsContainer: {
      width: "3rem",
      height: "3rem",
      borderRadius: "1.5rem",
      backgroundColor: theme.colors.tertiary[95],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    } as CSSProperties,

    rightAvatarInitialsText: {
      fontSize: theme.typography.title.small.emphasized.fontSize,
      fontWeight: theme.typography.title.small.emphasized.fontWeight,
      lineHeight: 1,
      color: theme.colors.tertiary[30],
    } as CSSProperties,
    breadcrumStyle: {
      maxWidth: "calc(100vw - 16rem)",
      overflowX: "auto",
      overflowY: "hidden",
      whiteSpace: "nowrap",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
    } as CSSProperties,
  };
};
